import { useMemo } from 'react';
import styled from 'styled-components';

import { AccessModes, Dimensions, Metrics, Sources } from 'components/insights/constants';
import MetricTile from 'components/insights/layout/tiles/MetricTile';
import Histogram from 'components/shared/charts/Histogram';
import { colors } from 'styles/theme/colors';

import useNetworkPerformanceAnalytics from '../query/useNetworkPerformanceAnalytics';

const LOS_BUCKETS = ['0-9', '10-19', '20-29', '30-39', '40-49', '50-59', '60-69', '70-79', '80-89', '90+'];

// Figma gradient: first bucket darkest, trailing buckets lighter
const barColors = (data: number[]) =>
  data.map((_, i) => {
    if (i === 0) return colors.primaryBlue;
    if (i <= 3) return colors.primaryBlue75;
    return colors.primaryBlue50;
  });

export default function EngagementsPerPatientTile() {
  const queryParams = useMemo(
    () => ({
      source: Sources.LOCATION_EPISODE_DISCHARGES,
      accessModes: [AccessModes.MANAGER],
      dimensions: [Dimensions.LENGTH_OF_STAY],
      metrics: [Metrics.ACTIVITIES_PER_PATIENT_PER_WEEK],
      rollups: true,
    }),
    []
  );

  const { loading, data, rollups } = useNetworkPerformanceAnalytics(queryParams, {
    queryKey: 'networkPerformanceEngagements',
  });

  const overallValue = rollups.overall.metrics.getCurrentDataPoint(Metrics.ACTIVITIES_PER_PATIENT_PER_WEEK, 0);
  const rawLosValues = data.dimensions.getCurrentDimensionValuesByIndex(0);
  const engagementValues = data.metrics.getCurrentSeries(Metrics.ACTIVITIES_PER_PATIENT_PER_WEEK);

  // Bin raw LOS values into standard buckets (same as ALOS tile)
  const histogramData = useMemo(
    () =>
      LOS_BUCKETS.map((bucket) =>
        engagementValues.reduce((acc: number, value: number, idx: number) => {
          const [min, max = null] = bucket.split('-').map((v) => parseInt(v, 10));
          const los = Number(rawLosValues[idx]);
          const include = max ? los >= min && los <= max : los >= min;
          return include ? acc + (value ?? 0) : acc;
        }, 0)
      ),
    [rawLosValues, engagementValues]
  );

  const hasData = overallValue != null;

  return (
    <MetricTile
      label='Engagements Per Patient Per Week'
      value={hasData ? overallValue.toFixed(1) : undefined}
      loading={loading}
      hasData={hasData}>
      <Histogram
        config={{
          chart: { height: 135, spacing: [4, 4, 4, 4] },
          xAxis: {
            categories: LOS_BUCKETS,
            title: { text: undefined },
            labels: {
              rotation: 0,
              style: {
                fontSize: '7px',
                color: colors.black50,
                fontWeight: 'normal',
              },
            },
          },
          yAxis: {
            labels: {
              style: { fontSize: '9px' },
            },
          },
          plotOptions: {
            column: {
              shadow: false,
              borderRadius: 2,
              pointWidth: 24,
              colorByPoint: true,
              colors: barColors(histogramData as number[]),
            },
          },
          series: [
            {
              type: 'column',
              name: 'Engagements',
              data: histogramData,
            },
          ],
        }}
        formatTooltipSupertext={(x) => `${x} Days`}
        formatTooltipSubtext={(y) => (y != null ? `Engagements/Patient/Week: ${y.toFixed(1)}` : undefined)}
      />
      <Footer>Engagement by Length of Stay</Footer>
    </MetricTile>
  );
}

const Footer = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.black};
  text-align: center;
  padding-bottom: 8px;
`;
